import { useCallback, useEffect, useRef, useState } from "react";
import { sendOtp, verifyOtp, resetRecaptcha, describeFirebaseError, runFirebaseDiagnostics } from "./firebase";

/**
 * useFirebasePhoneAuth — common logic for all three login portals
 * (Customer, Shopkeeper, Super admin).
 *
 * Caller renders an invisible <div id={recaptchaId}/> somewhere in the DOM.
 * Workflow:
 *   await requestOtp("9876543210")          // sends SMS, starts resend timer
 *   const { idToken } = await confirmCode("123456")  // verifies code, returns ID token
 *
 * The hook does NOT call your backend — pass the returned `idToken` to your
 * existing `/auth/verify-otp` endpoint (which now validates the token).
 */
export function useFirebasePhoneAuth(recaptchaContainerId = "recaptcha-container") {
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0); // seconds remaining
  const [error, setError] = useState("");
  const confirmationRef = useRef(null);
  const timerRef = useRef(null);

  // Tick down the resend counter
  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendIn]);

  // Clean up Recaptcha on unmount
  useEffect(() => () => resetRecaptcha(), []);

  const requestOtp = useCallback(async (mobile10) => {
    setError("");
    if (!/^[6-9]\d{9}$/.test(mobile10)) {
      const msg = "Enter a valid 10-digit Indian mobile number";
      setError(msg);
      throw new Error(msg);
    }
    setSending(true);
    try {
      // Always start from a fresh verifier. A previously-rendered reCAPTCHA
      // in the same DOM node makes Firebase throw "reCAPTCHA has already
      // been rendered in this element" on the next attempt.
      resetRecaptcha();
      const phone = `+91${mobile10}`;
      confirmationRef.current = await sendOtp(phone, recaptchaContainerId);
      setResendIn(30);
      return true;
    } catch (err) {
      // Loud debug surface — show the real Firebase code + message so we can
      // diagnose console-side issues without needing DevTools on mobile.
      console.error("Firebase OTP Error:", err);
      const rawFirebaseMessage = err?.code
        ? `${err.code} - ${err.message}`
        : (err?.message || "Unknown Firebase error");

      // For network errors we run the upgraded diagnostic (reachability +
      // reCAPTCHA + API key probe) and surface its `diagnosis` text directly.
      let probeSummary = "";
      if (err?.code === "auth/network-request-failed") {
        try {
          const { diagnosis } = await runFirebaseDiagnostics();
          if (diagnosis) probeSummary = `\n\n${diagnosis}`;
        } catch { /* ignore probe failures */ }
      }

      try { window.alert(`Firebase OTP failed:\n${rawFirebaseMessage}${probeSummary}`); } catch { /* ignore */ }

      resetRecaptcha();
      const msg = describeFirebaseError(err);
      setError(msg);
      throw new Error(msg);
    } finally {
      setSending(false);
    }
  }, [recaptchaContainerId]);

  const confirmCode = useCallback(async (code) => {
    setError("");
    if (!confirmationRef.current) {
      const msg = "OTP session expired. Tap Resend to receive a new code.";
      setError(msg);
      throw new Error(msg);
    }
    if (!/^\d{6}$/.test(code)) {
      const msg = "Enter the 6-digit OTP";
      setError(msg);
      throw new Error(msg);
    }
    setVerifying(true);
    try {
      const result = await verifyOtp(confirmationRef.current, code);
      return result; // { idToken, firebaseUser }
    } catch (err) {
      const msg = describeFirebaseError(err);
      setError(msg);
      throw new Error(msg);
    } finally {
      setVerifying(false);
    }
  }, []);

  const reset = useCallback(() => {
    confirmationRef.current = null;
    setResendIn(0);
    setError("");
    resetRecaptcha();
  }, []);

  return {
    requestOtp,
    confirmCode,
    reset,
    sending,
    verifying,
    resendIn,
    canResend: resendIn === 0,
    error,
  };
}
